import React, { useState } from "react";
import { FileTextOutlined, DatabaseOutlined } from "@ant-design/icons";
import "../../styles/compareWizard.css";
import UploadFile from "./UploadFile";
import SelectDB from "./SelectDB";

// One reusable source picker used for both sides of a reconciliation. An internal
// File/Database toggle switches between the EXISTING UploadFile and SelectDB step
// components (reused verbatim). It emits { kind:'file'|'db', payload } where the
// payload carries exactly the fields that source's original modal collected:
//   file → { file, header, delimiter }
//   db   → { header, dbName, sql, suiteName }
//
// The picker is a single wizard step; the reused sub-component owns its own
// Next/Back footer (StepFooter). `side` only controls back-button visibility via
// the sub-components' existing `compareType` prop ("db" = Source A, no back;
// "file" = Source B, back shown) — no rewrite of the sub-components.
function DataSourcePicker({
  side = "A",
  value,
  onChange,
  onBack,
  dbConnections = [],
  environments = [],
}) {
  const [kind, setKind] = useState(value?.kind || null);
  // Source A is the first step (no back); Source B shows a back button. The
  // sub-components read this via `compareType` ("db" hides back, "file" shows it).
  const compareType = side === "A" ? "db" : "file";

  const emitFile = (_index, file, _dbList, header, delimiter) => {
    onChange({ kind: "file", payload: { file, header, delimiter } });
  };

  const emitDb = (_index, header, dbName, sql, suiteName) => {
    onChange({ kind: "db", payload: { header, dbName, sql, suiteName } });
  };

  return (
    <div className="cmp-pane">
      <div className="cmp-block">
        <span className="cmp-field-label">
          Source {side} — choose a data source type
        </span>
        <div className="ds-toggle">
          <button
            type="button"
            className={`ds-toggle-card${kind === "file" ? " ds-toggle-card--active" : ""}`}
            onClick={() => setKind("file")}
          >
            <FileTextOutlined className="ds-toggle-icon" />
            <span className="ds-toggle-label">File</span>
            <span className="ds-toggle-desc">CSV, pipe or tab-delimited</span>
          </button>
          <button
            type="button"
            className={`ds-toggle-card${kind === "db" ? " ds-toggle-card--active" : ""}`}
            onClick={() => setKind("db")}
          >
            <DatabaseOutlined className="ds-toggle-icon" />
            <span className="ds-toggle-label">Database</span>
            <span className="ds-toggle-desc">Query a saved connection</span>
          </button>
        </div>
      </div>

      {kind === "file" && (
        <UploadFile
          clickNext={emitFile}
          clickPrevious={onBack}
          compareType={compareType}
        />
      )}
      {kind === "db" && (
        <SelectDB
          clickNext={emitDb}
          clickPrevious={onBack}
          dbConnections={dbConnections}
          environments={environments}
          compareType={compareType}
        />
      )}
    </div>
  );
}

export default DataSourcePicker;
